import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, IconButton, Typography,
  TextField, Divider, Chip, Paper, Autocomplete, Grid, Alert
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, LocationOn as LocationIcon,
  Send as SendIcon
} from '@mui/icons-material';
import DireccionInput from './DireccionInput';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';

const DestinosMultiples = ({
  destinos = [],
  onChange,
  maxDestinos = 0,
  disabled = false,
  idOrden = null,
  clientesExternos = null,
}) => {
  const { user } = useAuth();
  const [clientesPropios, setClientesPropios] = useState([]);
  const [sendingTracking, setSendingTracking] = useState(false);
  const [trackingMsg, setTrackingMsg] = useState(null);

  // Usar clientes externos si se proporcionan, sino cargar propios
  const clientes = clientesExternos || clientesPropios;

  useEffect(() => {
    if (clientesExternos) return; // No cargar si ya vienen como prop
    const fetchClientes = async () => {
      try {
        const res = await axios.get(`${config.API_BASE_URL}/api/clientes`, {
          headers: { Authorization: `Bearer ${user?.token}` },
        });
        setClientesPropios(res.data || []);
      } catch (e) {
        console.error('Error cargando clientes:', e);
      }
    };
    if (user?.token) fetchClientes();
  }, [user, clientesExternos]);

  const agregarDestino = () => {
    if (maxDestinos > 0 && destinos.length >= maxDestinos) return;
    onChange([
      ...destinos,
      {
        tipo_punto: 'DESTINO',
        direccion: '',
        referencia: '',
        latitud: null,
        longitud: null,
        nombre_contacto: '',
        telefono_contacto: '',
        instrucciones: '',
        id_cliente: null,
        nombre_cliente_destino: '',
        telefono_destino: '',
        email_destino: '',
        detalle_entrega: '',
        monto_entrega: 0,
        comision_destino: 0,
        tipo_comision_destino: 'FLAT',
        porcentaje_comision_destino: 0,
      },
    ]);
  };

  const eliminarDestino = (index) => {
    onChange(destinos.filter((_, i) => i !== index));
  };

  const actualizarDestino = (index, data) => {
    const nuevos = [...destinos];
    nuevos[index] = { ...nuevos[index], ...data };
    onChange(nuevos);
  };

  const handleClienteSelect = (index, cliente) => {
    if (!cliente) {
      actualizarDestino(index, { id_cliente: null, nombre_cliente_destino: '', telefono_destino: '', email_destino: '' });
      return;
    }
    actualizarDestino(index, {
      id_cliente: cliente.id_cliente,
      nombre_cliente_destino: `${cliente.nombre_cliente} ${cliente.apellido_cliente || ''}`.trim(),
      nombre_contacto: `${cliente.nombre_cliente} ${cliente.apellido_cliente || ''}`.trim(),
      telefono_destino: cliente.telefono_cliente || '',
      telefono_contacto: cliente.telefono_cliente || '',
      email_destino: cliente.email_cliente || '',
    });
  };

  const handleEnviarTracking = async () => {
    if (!idOrden) return;
    setSendingTracking(true);
    setTrackingMsg(null);
    try {
      const res = await axios.post(
        `${config.API_BASE_URL}/api/v1/delivery/${idOrden}/destinos/enviar-tracking`,
        {},
        { headers: { Authorization: `Bearer ${user?.token}` } }
      );
      setTrackingMsg({ type: 'success', text: res.data.mensaje });
    } catch (e) {
      setTrackingMsg({ type: 'error', text: e.response?.data?.error || 'Error al enviar tracking' });
    } finally {
      setSendingTracking(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1A237E' }}>
            Puntos de Entrega
          </Typography>
          <Chip label={destinos.length} size="small" color="primary" />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {idOrden && destinos.length > 0 && (
            <Button
              startIcon={<SendIcon />}
              onClick={handleEnviarTracking}
              disabled={sendingTracking || disabled}
              variant="outlined" size="small" color="success"
            >
              {sendingTracking ? 'Enviando...' : 'Enviar tracking a clientes'}
            </Button>
          )}
          <Button
            startIcon={<AddIcon />}
            onClick={agregarDestino}
            disabled={disabled || (maxDestinos > 0 && destinos.length >= maxDestinos)}
            variant="contained" size="small"
            sx={{ bgcolor: '#1A237E' }}
          >
            Agregar Entrega
          </Button>
        </Box>
      </Box>

      {trackingMsg && (
        <Alert severity={trackingMsg.type} sx={{ mb: 2 }} onClose={() => setTrackingMsg(null)}>
          {trackingMsg.text}
        </Alert>
      )}

      {destinos.length === 0 && (
        <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#F8FAFC', borderRadius: 2, border: '2px dashed #E2E8F0' }}>
          <LocationIcon sx={{ fontSize: 40, color: '#94A3B8', mb: 1 }} />
          <Typography color="text.secondary" sx={{ mb: 1 }}>No hay puntos de entrega</Typography>
          <Button startIcon={<AddIcon />} onClick={agregarDestino} disabled={disabled} size="small">
            Agregar primer punto
          </Button>
        </Box>
      )}

      {destinos.map((destino, index) => (
        <Card key={index} sx={{ mb: 2, borderRadius: 2, border: '1px solid #E2E8F0', borderLeft: `4px solid ${destino.entregado ? '#10B981' : '#1A237E'}` }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={`Entrega ${index + 1}`} size="small" color="primary" variant="outlined" />
                {destino.entregado && <Chip label="Entregado" size="small" color="success" />}
                {destino.tracking_enviado && <Chip label="Tracking enviado" size="small" variant="outlined" color="info" />}
                {!destino.latitud && destino.direccion && <Chip label="Sin coordenadas" size="small" color="error" variant="outlined" />}
              </Box>
              <IconButton size="small" color="error" onClick={() => eliminarDestino(index)} disabled={disabled}>
                <DeleteIcon />
              </IconButton>
            </Box>

            {/* Cliente */}
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Cliente *
            </Typography>
            <Autocomplete
              options={clientes}
              getOptionLabel={(opt) => `${opt.nombre_cliente} ${opt.apellido_cliente || ''} — ${opt.telefono_cliente || opt.email_cliente || ''}`.trim()}
              value={clientes.find(c => c.id_cliente === destino.id_cliente) || null}
              onChange={(_, val) => handleClienteSelect(index, val)}
              disabled={disabled}
              size="small"
              sx={{ mb: 2 }}
              renderInput={(params) => <TextField {...params} placeholder="Buscar cliente..." />}
              noOptionsText="No hay clientes"
            />

            {/* Dirección con coordenadas obligatorias */}
            <DireccionInput
              label="Dirección de entrega *"
              value={{
                direccion: destino.direccion,
                latitud: destino.latitud,
                longitud: destino.longitud,
                referencia: destino.referencia,
                nombre_contacto: destino.nombre_contacto,
                telefono_contacto: destino.telefono_contacto,
              }}
              onChange={(val) => actualizarDestino(index, val)}
              requiereCoordenadas={true}
              disabled={disabled}
              showContacto={false}
              showMap={true}
            />

            <Divider sx={{ my: 2 }} />

            {/* Detalle entrega */}
            <TextField
              fullWidth size="small"
              label="¿Qué se entrega? *"
              placeholder="Ej: 2 pollos a la brasa + papas + gaseosa"
              value={destino.detalle_entrega || ''}
              onChange={(e) => actualizarDestino(index, { detalle_entrega: e.target.value })}
              disabled={disabled}
              multiline rows={2}
              sx={{ mb: 2 }}
            />

            {/* Montos y comisión */}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={4}>
                <TextField
                  fullWidth size="small" label="Monto entrega *" type="number"
                  value={destino.monto_entrega || ''}
                  onChange={(e) => {
                    const monto = parseFloat(e.target.value) || 0;
                    const updates = { monto_entrega: monto };
                    // Si es porcentaje, recalcular comisión
                    if (destino.tipo_comision_destino === 'PORCENTAJE' && destino.porcentaje_comision_destino) {
                      updates.comision_destino = Math.round(monto * destino.porcentaje_comision_destino / 100 * 100) / 100;
                    }
                    actualizarDestino(index, updates);
                  }}
                  disabled={disabled}
                  InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: '#94A3B8', fontSize: 13 }}>S/</Typography> }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth size="small" label="Comisión delivery" type="number"
                  value={destino.comision_destino || ''}
                  onChange={(e) => {
                    const comision = parseFloat(e.target.value) || 0;
                    const monto = parseFloat(destino.monto_entrega) || 0;
                    actualizarDestino(index, {
                      comision_destino: comision,
                      tipo_comision_destino: 'FLAT',
                      porcentaje_comision_destino: monto > 0 ? Math.round(comision / monto * 10000) / 100 : 0,
                    });
                  }}
                  disabled={disabled}
                  InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: '#94A3B8', fontSize: 13 }}>S/</Typography> }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth size="small" label="% equivalente" type="number"
                  value={destino.porcentaje_comision_destino || ''}
                  onChange={(e) => {
                    const pct = parseFloat(e.target.value) || 0;
                    const monto = parseFloat(destino.monto_entrega) || 0;
                    actualizarDestino(index, {
                      porcentaje_comision_destino: pct,
                      tipo_comision_destino: 'PORCENTAJE',
                      comision_destino: Math.round(monto * pct / 100 * 100) / 100,
                    });
                  }}
                  disabled={disabled}
                  InputProps={{ endAdornment: <Typography sx={{ ml: 0.5, color: '#94A3B8', fontSize: 13 }}>%</Typography> }}
                />
              </Grid>
            </Grid>

            {/* Contacto */}
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <TextField
                  fullWidth size="small" label="Nombre"
                  value={destino.nombre_cliente_destino || ''}
                  onChange={(e) => actualizarDestino(index, { nombre_cliente_destino: e.target.value, nombre_contacto: e.target.value })}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth size="small" label="Teléfono"
                  value={destino.telefono_destino || ''}
                  onChange={(e) => actualizarDestino(index, { telefono_destino: e.target.value, telefono_contacto: e.target.value })}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth size="small" label="Email (tracking)"
                  value={destino.email_destino || ''}
                  onChange={(e) => actualizarDestino(index, { email_destino: e.target.value })}
                  disabled={disabled}
                  placeholder="cliente@email.com"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      {/* Resumen de totales */}
      {destinos.length > 0 && (
        <Box sx={{
          mt: 2, p: 2, borderRadius: 2,
          background: 'linear-gradient(135deg, #EEF2FF 0%, #F0F9FF 100%)',
          border: '1px solid #C7D2FE',
        }}>
          <Grid container spacing={2}>
            <Grid item xs={4} sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Entregas</Typography>
              <Typography variant="h6" fontWeight={800} color="#1A237E">{destinos.length}</Typography>
            </Grid>
            <Grid item xs={4} sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Monto Total</Typography>
              <Typography variant="h6" fontWeight={800} color="#1A237E">
                S/ {destinos.reduce((s, d) => s + (parseFloat(d.monto_entrega) || 0), 0).toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={4} sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Comisión Motor.</Typography>
              <Typography variant="h6" fontWeight={800} color="#FF6B35">
                S/ {destinos.reduce((s, d) => s + (parseFloat(d.comision_destino) || 0), 0).toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}

      {maxDestinos > 0 && destinos.length >= maxDestinos && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
          Máximo de {maxDestinos} puntos de entrega
        </Typography>
      )}
    </Paper>
  );
};

export default DestinosMultiples;
